import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField } from "@repo/components/src/ui/input";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectFlatList,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@repo/components/src/ui/select";
import { VStack } from "@repo/components/src/ui/vstack";
import { IDENTIFIER_HANDLERS } from "@repo/components/src/utils/identifier/handlers";
import { IdentifierTypeEnum } from "@repo/graphql/gql/graphql";
import { AlertCircleIcon, ChevronDown } from "lucide-react-native";
import { FieldPathByValue, FieldValues, useController } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import {
  findIdentifierOption,
  getIdentifierErrorMessage,
  IdentifierFieldValue,
  isIdentifierTypeOption,
  nextTypedIdentifierValue,
  readIdentifierFieldValue,
  resolveIdentifierType,
} from "./logic";
import { useIdentifierTypes } from "./useIdentifierTypes";

type FormInputIdentifierProps<TFieldValues extends FieldValues> = Omit<
  FormInputProps<TFieldValues, IdentifierFieldValue | undefined>,
  "name" | "placeholder"
> & {
  name: FieldPathByValue<TFieldValues, IdentifierFieldValue | undefined>;
  countryCode: string | null | undefined;
  /** Restricts the document types offered, e.g. `TAX_ID_TYPES`. Keep it stable. */
  allowedTypes?: readonly IdentifierTypeEnum[];
};

/**
 * The input for a document number and its type. It reads and writes
 * `{ value, type }`: the type selector only shows when the country accepts
 * more than one document, and the number input takes its placeholder,
 * length and keyboard from the selected one. What is typed is cleaned by the
 * type's handler, and numeric documents are shown formatted (`12.345.678-5`)
 * while the form keeps the clean value. The default type is only shown,
 * and written along with the number once it is typed, so opening a form
 * never dirties it or stamps a type on a number saved without one.
 */
export default function FormInputIdentifier<TFieldValues extends FieldValues>(
  props: FormInputIdentifierProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    labelSize = "md",
    countryCode,
    allowedTypes,
  } = props;

  const { options } = useIdentifierTypes(countryCode, allowedTypes);

  const {
    field: { onChange, value, ref, onBlur, disabled },
    fieldState: { error },
  } = useController({ name, control, disabled: disabledProp });

  const { type: currentType, value: currentValue } =
    readIdentifierFieldValue(value);
  const selectedType = resolveIdentifierType(
    options,
    currentType,
    currentValue,
  );

  const selectedOption = findIdentifierOption(options, selectedType);
  const handler = selectedType ? IDENTIFIER_HANDLERS[selectedType] : undefined;
  const displayValue = handler?.formatWhileTyping
    ? handler.format(currentValue)
    : currentValue;
  const showTypeSelector = options.length > 1;
  const errorMessage = getIdentifierErrorMessage(error);

  const inputLabel =
    label ??
    (showTypeSelector
      ? "N° de identificación"
      : (selectedOption?.label ?? "Identificador"));

  return (
    <VStack space="md" className={className}>
      {showTypeSelector ? (
        <FormControl isRequired={required} isDisabled={disabled} size={size}>
          <FormControlLabel>
            <FormControlLabelText size={labelSize}>
              Tipo de identificación
            </FormControlLabelText>
          </FormControlLabel>
          <Select
            key={selectedType}
            initialLabel={selectedOption?.label}
            selectedValue={selectedType}
            onValueChange={(nextType) => {
              const nextOption = options.find(
                (option) => option.type === nextType,
              );
              if (nextType !== selectedType && nextOption) {
                onChange({
                  value:
                    IDENTIFIER_HANDLERS[nextOption.type].clean(currentValue),
                  type: nextOption.type,
                });
              }
            }}
          >
            <SelectTrigger variant="outline" size={size}>
              <SelectInput
                placeholder="Selecciona una opción"
                className="flex-1 shrink font-body"
              />
              <SelectIcon as={ChevronDown} className="mr-3" />
            </SelectTrigger>
            <SelectPortal>
              <SelectBackdrop />
              <SelectContent className="pb-safe-offset-4 max-h-[85%]">
                <SelectDragIndicatorWrapper>
                  <SelectDragIndicator />
                </SelectDragIndicatorWrapper>
                <SelectFlatList
                  className="mt-2"
                  data={options}
                  renderItem={({ item }) =>
                    isIdentifierTypeOption(item) ? (
                      <SelectItem
                        label={item.label}
                        value={item.type}
                        key={item.type}
                      />
                    ) : null
                  }
                />
              </SelectContent>
            </SelectPortal>
          </Select>
        </FormControl>
      ) : null}
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={required}
        isDisabled={disabled}
        size={size}
      >
        <FormControlLabel>
          <FormControlLabelText size={labelSize}>
            {inputLabel}
          </FormControlLabelText>
        </FormControlLabel>
        <Input isDisabled={disabled} isInvalid={!!errorMessage} size={size}>
          <InputField
            ref={focusScrollRef(ref)}
            onBlur={onBlur}
            placeholder={selectedOption?.example ?? "Número de identificación"}
            value={displayValue}
            onChangeText={(nextValue: string) =>
              onChange({
                value: !handler
                  ? nextValue
                  : handler.formatWhileTyping
                    ? nextTypedIdentifierValue(handler, currentValue, nextValue)
                    : handler.clean(nextValue),
                type: selectedType,
              })
            }
            keyboardType={selectedOption?.numeric ? "numeric" : "default"}
            textContentType="none"
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={selectedOption?.maxLength}
          />
        </Input>
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
          <FormControlErrorText>{errorMessage}</FormControlErrorText>
        </FormControlError>
      </FormControl>
    </VStack>
  );
}
